/* Created by Interactive 63 - https://interactive63.com */
/**
 * A visually hidden polite live region for status changes that have no visible message of their
 * own, such as playback starting or stopping. Messages sharing a key are rate limited, so a
 * status that flips often (like autosave) does not talk over the user.
 */
import { h } from './dom';

let region: HTMLElement | null = null;
const lastSpoken = new Map<string, number>();

/** Returns the shared live region, creating it on first use. */
function getRegion(): HTMLElement {
  if (region?.isConnected) return region;
  region = h('div', { class: 'visually-hidden', role: 'status', 'aria-live': 'polite' });
  document.body.append(region);
  return region;
}

/**
 * Announces a message to assistive technology. With `minIntervalMs`, a message is dropped if one
 * with the same key was announced more recently than that.
 */
export function announce(
  message: string,
  options: { key?: string; minIntervalMs?: number } = {},
): void {
  const { key = message, minIntervalMs = 0 } = options;
  const now = Date.now();
  const last = lastSpoken.get(key);
  if (last !== undefined && now - last < minIntervalMs) return;
  lastSpoken.set(key, now);
  // Clearing first makes a repeated message count as a change.
  const live = getRegion();
  live.textContent = '';
  live.textContent = message;
}

/** Forgets what has been announced. For tests. */
export function resetAnnouncer(): void {
  lastSpoken.clear();
  region?.remove();
  region = null;
}
